/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import cx from 'classnames';
import { useEffect } from 'react';
import { Popper, PopperBody, PopperHeader } from '@/components/AZCustomComponent/Popper/Popper';
import { SmartLink } from '@/utils/smartLink';
import { setCookie } from '@/utils/cookie';
import { Text, View } from '@az/starc-ui';
import styles from './SignInAlertMobile.module.scss';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useShouldShowSignInAlert } from './useShouldShowSignInAlert';
import { useRouter } from 'next/navigation';
import { Label } from '@/features/i18n';
import { eventConstants } from '@/constants/event';
import { sessionStorage } from '@/utils/sessionStorage';
import { isSafari } from '@/utils/common';
import { localStorage } from '@/utils/localStorage';
import { useGlobalAlertBanner } from '@/features/header/hooks/useGlobalAlertBanner';
import { isServer } from '@/utils/isServer';

type Props = {
  anchorRef: HTMLElement | null;
  drawerOpen: boolean;
};

export function SignInAlertMobile({ anchorRef, drawerOpen }: Props) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const router = useRouter();
  const { shouldShowAlert, hideForSession } = useShouldShowSignInAlert();
  const { isAlertBannerVisible } = useGlobalAlertBanner();

  const showAlert = shouldShowAlert && !drawerOpen && isMobile && anchorRef;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (shouldShowAlert && sessionStorage.getItem('signInPopupShown') !== 'true') {
        sessionStorage.setItem('signInPopupDisabled', 'true');
      }
    }, 2000);
    clearTimeout(timeoutId);
  }, [shouldShowAlert]);

  useEffect(() => {
    // close the alert if the user presses the escape key
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === eventConstants.escKeyType) {
        hideForSession();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [hideForSession]);

  if (!showAlert) {
    return null;
  }

  const handleCreate = (event: React.SyntheticEvent) => {
    event.preventDefault();
    hideForSession();
    void router.push('/signin');
    void setCookie('loginInteractionMethod', 'Login Alert');
    localStorage.setItem('loginInteractionMethod', 'Login Alert');
  };

  return (
    <Popper
      elevation={4}
      isOpen={!isServer}
      anchorEl={anchorRef}
      placement="bottom"
      classes={{
        arrow: styles.tooltipArrow,
        popper: cx(styles.signInAlertPopper, { [styles.withAlertBanner]: isAlertBannerVisible }),
        paper: styles.signInAlertPaper,
      }}
      modifiers={{
        arrow: { enabled: true },
      }}
    >
      <View className={styles.iconContainer}>
        <PopperHeader
          handleClose={hideForSession}
          classes={{
            heading: cx(styles.mobileAlertHeader, { [styles.safariHeader]: isSafari() }),
          }}
        >
          <Label label="sign_in_alert_header" />
        </PopperHeader>
      </View>
      <PopperBody>
        <Text variant="text-link" className={styles.signInAlertSecondLine} color="600">
          <Label label="label_rewards_waiting" />
          <span className={styles.links}>
            <SmartLink
              to="/signin"
              onClick={handleCreate}
              rel="nofollow"
              className={styles.joinNowLink}
            >
              <Label label="label_SignInorCreateAccount" />
            </SmartLink>
          </span>
        </Text>
      </PopperBody>
    </Popper>
  );
}
